// @flow

import first from 'lodash/fp/first';
import zip from 'lodash/fp/zip';
import filter from 'lodash/fp/filter';
import reduce from 'lodash/fp/reduce';

type FullscreenElement = Element | null;

type FullscreenElementProperties =
  () => $ReadOnlyArray<FullscreenElement | void>;

type CurrentFullscreenElement = () => FullscreenElement | void;

type CrossPlatformFullscreenEvent =
  | 'fullscreenchange'
  | 'mozfullscreenchange'
  | 'webkitfullscreenchange'
  | 'msfullscreenchange';

type FullscreenEventName = () => CrossPlatformFullscreenEvent | '';

export const SCROLLBAR_SIZE: 15 = 15; // px

export const htmlAsPlainText = (html: string): string => {
  const node = document.createElement('div');
  // TODO: this is simplistic but good enough for now
  /* eslint-disable-next-line fp/no-mutation */
  node.innerHTML = html.replace(/<div>(\n)?/g, '<div>\n');
  // TODO: innerText doesn't seem to be supported in our test enviroment :\
  // return node.innerText || '';
  return node.textContent || '';
};

export const withoutDefault =
  <+Target: EventTarget, Event: SyntheticEvent<Target>>(
    handler: (Event) => void,
  ): ((Event) => void) =>
  (e: Event): void => {
    e.preventDefault();
    handler(e);
  };

export const ancestorsUntil = (
  ancestor: HTMLElement,
  node: HTMLElement,
): HTMLElement[] =>
  node === ancestor || !node.parentElement
    ? [node]
    : // node.parentElement is an `Element` rather than an `HTMLElement`
      [
        ...ancestorsUntil(ancestor, ((node.parentElement: any): HTMLElement)),
        node,
      ];

export const hasAncestor = (
  ancestor: HTMLElement,
  node: HTMLElement,
): boolean => ancestorsUntil(ancestor, node).includes(ancestor);

const crossPlatformFullscreenEventNames: $ReadOnlyArray<CrossPlatformFullscreenEvent> =
  [
    'fullscreenchange',
    'mozfullscreenchange',
    'webkitfullscreenchange',
    'msfullscreenchange',
  ];

export const fullscreenElementProperties: FullscreenElementProperties = () => [
  document.fullscreenElement,
  // Flow is not aware that these vendor prefixed properties exist in some environments
  // $FlowFixMe[prop-missing]
  document.mozFullScreenElement,
  // $FlowFixMe[prop-missing]
  document.webkitFullscreenElement,
  // $FlowFixMe[prop-missing]
  document.msFullscreenElement,
];

const isFullscreenElement: (element: FullscreenElement | void) => boolean = (
  element,
) => element !== undefined;

export const fullscreenElement: CurrentFullscreenElement = () =>
  first(filter(isFullscreenElement, fullscreenElementProperties()));

export const fullscreenEventName: FullscreenEventName = () =>
  reduce(
    (acc, [element, eventName]) => {
      if (acc === '' && isFullscreenElement(element)) return eventName;
      return acc;
    },
    '',
    zip(fullscreenElementProperties(), crossPlatformFullscreenEventNames),
  );

export const fullscreenSupported: boolean =
  fullscreenElementProperties().some(isFullscreenElement);

const crossPlatformRequestFullscreen = (domNode: Element) =>
  // $FlowFixMe[method-unbinding]
  domNode.requestFullscreen ||
  // Flow is not aware that these vendor prefixed methods exist in some environments
  // $FlowFixMe[prop-missing]
  domNode.mozRequestFullScreen ||
  // $FlowFixMe[prop-missing]
  domNode.msRequestFullscreen ||
  // $FlowFixMe[prop-missing]
  domNode.webkitRequestFullscreen;

const crossPlatformExitFullscreen =
  // $FlowFixMe[method-unbinding]
  document.exitFullscreen ||
  // FlowFixMe—Flow is not aware that these vendor prefixed methods exist in some environments
  // $FlowFixMe[prop-missing]
  document.mozCancelFullScreen ||
  // $FlowFixMe[prop-missing]
  document.webkitExitFullscreen ||
  // $FlowFixMe[prop-missing]
  document.msExitFullscreen;

export const requestFullscreen = (domNode: Element): Promise<void> =>
  crossPlatformRequestFullscreen(domNode).call(domNode) ||
  Promise.resolve(undefined);

export const exitFullscreen = (): Promise<void> =>
  crossPlatformExitFullscreen.call(document) || Promise.resolve(undefined);

export const scriptTagWithJS = (js: string): HTMLScriptElement => {
  const script = document.createElement('script');
  // eslint-disable-next-line fp/no-mutation
  script.innerHTML = js;
  return script;
};

export const documentHead = (): HTMLHeadElement => {
  const existingHeadElement = document.head;

  if (existingHeadElement) return existingHeadElement;

  const newHeadElement = document.createElement('head');
  if (document.body) {
    document.insertBefore(newHeadElement, document.body);
  } else {
    const target = document.firstChild || document;
    target.insertBefore(newHeadElement, target.childNodes[0]);
  }

  return newHeadElement;
};

export const appendScriptToHead = (scriptElement: HTMLScriptElement): void => {
  documentHead().appendChild(scriptElement);
};

export const prependScriptToHead = (scriptElement: HTMLScriptElement): void => {
  documentHead().insertBefore(
    scriptElement,
    documentHead().childNodes[0] || null,
  );
};

export const isContentEditable = (node: HTMLElement): boolean =>
  // according to MDN the values `'true'` and `''` both result in contenteditable
  // being turned on:
  // https://developer.mozilla.org/en-US/docs/Web/HTML/Global_attributes#attr-contenteditable
  node.hasAttribute('contenteditable') &&
  ['', 'true'].some((s) => node.getAttribute('contenteditable') === s);

export const eventIsFromInput = (e: Event): boolean => {
  const { target } = e;

  if (!(target instanceof HTMLElement)) return false;
  if (target instanceof HTMLInputElement && !target.hasAttribute('readonly'))
    return true;
  if (target.getAttribute('role') === 'textbox') return true;

  if (isContentEditable(target)) return true;

  return false;
};

/**
 * Set the value of an HTMLInputElement using the original value setter.
 *
 * React overrides the value setter on HTMLInputElement for its synthetic
 * event normalization stuff that it does. Because of this, it can be
 * impossible to imperatively trigger a change event on an HTMLInputElement in
 * a react context, something that can sometimes be necessary.
 */
export const setInputValueNatively = (
  input: HTMLInputElement,
  value: string,
): void => {
  (Object.getOwnPropertyDescriptor(
    window.HTMLInputElement.prototype,
    'value',
  ): any).set.call(input, value);
};

export const isAbortError = (error: Error): boolean => {
  if (window.AbortError !== undefined && error instanceof window.AbortError)
    return true;
  // flow is not yet aware of DOMException
  /* $FlowIgnore[cannot-resolve-name] */
  if (error instanceof DOMException && error.name === 'AbortError') return true;
  return false;
};
